import { Link } from 'react-router-dom';
import { useSEO } from '../../utils/seo';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import WhatsAppChat from '../../components/feature/WhatsAppChat';

export default function TermsOfServicePage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  useSEO({
    title: 'Terms of Service - Urban Spaces',
    description: 'Read the terms and conditions for using Urban Spaces services. Learn about our service agreements, user responsibilities, and legal terms.',
    keywords: 'terms of service, terms and conditions, user agreement, legal terms, Urban Spaces',
    canonical: `${siteUrl}/terms-of-service`,
    ogType: 'website'
  });

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <div className="pt-32 pb-24">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <Link to="/" className="inline-flex items-center space-x-2 text-teal-600 hover:text-teal-700 font-medium text-sm mb-8 cursor-pointer whitespace-nowrap">
            <i className="ri-arrow-left-line"></i>
            <span>Back to Home</span>
          </Link>

          <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
            Terms of Service
          </h1>
          
          <p className="text-gray-600 mb-8">
            Last Updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>

          <div className="prose prose-lg max-w-none">
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                1. Acceptance of Terms
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                By accessing and using Urban Spaces' services, you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to these terms, please do not use our services.
              </p>
              <p className="text-gray-700 leading-relaxed">
                These Terms of Service apply to all users of the site, including without limitation users who are browsers, vendors, customers, merchants, and contributors of content.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                2. Services Description
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Urban Spaces provides interior design consultation, web development services, and related creative solutions. Our services include but are not limited to:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Interior design consultation and planning</li>
                <li>Web development and digital solutions</li>
                <li>Space planning and 3D visualization</li>
                <li>Project management and implementation</li>
                <li>Custom design solutions tailored to client needs</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                3. User Responsibilities
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                When using our services, you agree to:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Provide accurate and complete information during consultations and bookings</li>
                <li>Maintain the confidentiality of any account credentials</li>
                <li>Notify us immediately of any unauthorized use of your account</li>
                <li>Use our services only for lawful purposes</li>
                <li>Respect intellectual property rights of all content provided</li>
                <li>Communicate professionally and respectfully with our team</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                4. Payment Terms
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Payment terms for our services are as follows:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Initial consultation fees are due at the time of booking</li>
                <li>Project deposits are required before work commences</li>
                <li>Payment schedules will be outlined in individual project agreements</li>
                <li>All prices are in USD unless otherwise specified</li>
                <li>Late payments may incur additional fees as specified in project contracts</li>
                <li>Refund policies are detailed in individual service agreements</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                5. Intellectual Property
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                All content, designs, concepts, and materials created by Urban Spaces remain our intellectual property until full payment is received. Upon completion of payment:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Clients receive full rights to use the final deliverables for their intended purpose</li>
                <li>Urban Spaces retains the right to showcase completed projects in our portfolio</li>
                <li>Any modifications to delivered work must be approved by Urban Spaces</li>
                <li>Third-party assets used in projects are subject to their respective licenses</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                6. Project Timelines and Deliverables
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Project timelines are estimates based on the scope of work agreed upon. Actual completion times may vary due to:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Client feedback and revision requests</li>
                <li>Availability of materials or third-party services</li>
                <li>Changes in project scope or requirements</li>
                <li>Unforeseen circumstances beyond our control</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                We commit to communicating any delays promptly and working with clients to adjust timelines as needed.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                7. Cancellation and Refund Policy
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Cancellation policies vary by service type:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Consultations can be rescheduled up to 48 hours before the scheduled time</li>
                <li>Cancellations within 48 hours may forfeit the consultation fee</li>
                <li>Project cancellations will be charged for work completed to date</li>
                <li>Deposits are non-refundable once work has commenced</li>
                <li>Specific refund terms will be outlined in individual project agreements</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                8. Limitation of Liability
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Urban Spaces shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Use or inability to use our services</li>
                <li>Unauthorized access to or alteration of your transmissions or data</li>
                <li>Statements or conduct of any third party on the service</li>
                <li>Any other matter relating to the service</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                Our total liability shall not exceed the amount paid by you for the specific service in question.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                9. Confidentiality
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We respect the confidentiality of all client information and project details. Both parties agree to:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Keep confidential information private and secure</li>
                <li>Not disclose sensitive project details without written consent</li>
                <li>Use confidential information only for the purposes of the project</li>
                <li>Return or destroy confidential materials upon project completion</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                10. Modifications to Terms
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Urban Spaces reserves the right to modify these terms at any time. Changes will be effective immediately upon posting to our website. Your continued use of our services after changes are posted constitutes acceptance of the modified terms.
              </p>
              <p className="text-gray-700 leading-relaxed">
                We encourage you to review these terms periodically to stay informed of any updates.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                11. Governing Law
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                These Terms of Service shall be governed by and construed in accordance with the laws of the jurisdiction in which Urban Spaces operates, without regard to its conflict of law provisions.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Any disputes arising from these terms or our services shall be resolved through binding arbitration in accordance with the rules of the American Arbitration Association.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                12. Contact Information
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                If you have any questions about these Terms of Service, please contact us:
              </p>
              <div className="bg-gray-50 p-6 rounded-lg">
                <p className="text-gray-700 mb-2"><strong>Urban Spaces</strong></p>
                <p className="text-gray-700 mb-2">D-503, Capital Crown, Nr. Raysan Metro Station</p>
                <p className="text-gray-700 mb-2">Raysan, Gujarat, India - 382426</p>
                <p className="text-gray-700 mb-2">Email: enquiryurbanspaces@gmail.com</p>
                <p className="text-gray-700">Phone: +91 99258 98625</p>
              </div>
            </section>

            <div className="bg-teal-50 border-l-4 border-teal-500 p-6 rounded-r-lg mt-12">
              <p className="text-gray-800">
                <strong>Important:</strong> By using Urban Spaces' services, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service. If you do not agree with any part of these terms, please discontinue use of our services immediately.
              </p>
            </div>
          </div>
        </div>
      </div>

      <Footer />
      <WhatsAppChat />
    </div>
  );
}
